<script setup>
import { computed, ref } from 'vue';
import { useStoreGetters } from 'dashboard/composables/store';

const getters = useStoreGetters();
const currentChat = computed(() => getters.getSelectedChat.value);

const referral = computed(() => {
  return currentChat.value?.custom_attributes?.whatsapp_ad_referral || {};
});

const hasReferral = computed(() => Object.keys(referral.value).length > 0);

const mediaType = computed(() =>
  String(
    referral.value.media_type || referral.value.mediaType || ''
  ).toLowerCase()
);

const mediaUrl = computed(() => {
  if (mediaType.value === 'video') {
    return (
      referral.value.thumbnail_url ||
      referral.value.thumbnailUrl ||
      referral.value.video_url ||
      referral.value.videoUrl
    );
  }
  if (mediaType.value === 'image') {
    return referral.value.image_url || referral.value.imageUrl;
  }
  return (
    referral.value.thumbnail_url ||
    referral.value.thumbnailUrl ||
    referral.value.image_url ||
    referral.value.imageUrl ||
    referral.value.video_url ||
    referral.value.videoUrl ||
    null
  );
});

const headline = computed(() => referral.value.headline);
const body = computed(() => referral.value.body);
const sourceUrl = computed(
  () => referral.value.source_url || referral.value.sourceUrl
);
const sourceId = computed(
  () => referral.value.source_id || referral.value.sourceId
);
const ctwaClid = computed(
  () => referral.value.ctwa_clid || referral.value.ctwaClid
);

const hasImageError = ref(false);
</script>

<template>
  <div v-if="hasReferral" class="px-4 py-2">
    <a
      v-if="sourceUrl"
      :href="sourceUrl"
      target="_blank"
      rel="noopener noreferrer"
      class="block hover:opacity-90 transition-opacity"
    >
      <div
        v-if="mediaUrl"
        class="mb-3 w-full h-40 bg-n-slate-3 rounded-md overflow-hidden relative"
      >
        <img
          v-if="!hasImageError"
          :src="mediaUrl"
          alt="Ad Media"
          class="w-full h-full object-cover"
          @error="hasImageError = true"
        />
        <div
          v-else
          class="flex flex-col items-center justify-center w-full h-full bg-n-slate-2 text-n-slate-11 p-2 text-center border-b border-n-slate-3"
        >
          <span class="i-lucide-image-off size-5 mb-1 opacity-75" />
          <span class="text-[10px] leading-tight">{{
            $t('CONVERSATION.IMAGE_EXPIRED') || 'Image expired'
          }}</span>
        </div>
        <div
          v-if="mediaType === 'video'"
          class="absolute inset-0 flex items-center justify-center pointer-events-none"
        >
          <div
            class="flex items-center justify-center size-10 rounded-full bg-black/50 backdrop-blur-sm ring-1 ring-white/20 shadow-md"
          >
            <span class="i-lucide-play size-5 text-white ml-0.5" />
          </div>
        </div>
      </div>

      <h4 v-if="headline" class="text-sm font-semibold text-n-slate-12 mb-1">
        {{ headline }}
      </h4>

      <p v-if="body" class="text-xs text-n-slate-11 line-clamp-3 mb-2">
        {{ body }}
      </p>
    </a>

    <div v-else>
      <div
        v-if="mediaUrl"
        class="mb-3 w-full h-40 bg-n-slate-3 rounded-md overflow-hidden relative"
      >
        <img
          v-if="!hasImageError"
          :src="mediaUrl"
          alt="Ad Media"
          class="w-full h-full object-cover"
          @error="hasImageError = true"
        />
        <div
          v-else
          class="flex flex-col items-center justify-center w-full h-full bg-n-slate-2 text-n-slate-11 p-2 text-center border-b border-n-slate-3"
        >
          <span class="i-lucide-image-off size-5 mb-1 opacity-75" />
          <span class="text-[10px] leading-tight">{{
            $t('CONVERSATION.IMAGE_EXPIRED') || 'Image expired'
          }}</span>
        </div>
        <div
          v-if="mediaType === 'video'"
          class="absolute inset-0 flex items-center justify-center pointer-events-none"
        >
          <div
            class="flex items-center justify-center size-10 rounded-full bg-black/50 backdrop-blur-sm ring-1 ring-white/20 shadow-md"
          >
            <span class="i-lucide-play size-5 text-white ml-0.5" />
          </div>
        </div>
      </div>
      <h4 v-if="headline" class="text-sm font-semibold text-n-slate-12 mb-1">
        {{ headline }}
      </h4>
      <p v-if="body" class="text-xs text-n-slate-11 mb-2">
        {{ body }}
      </p>
    </div>

    <!-- Metadata Details -->
    <div class="mt-3 flex flex-col gap-1.5 text-xs text-n-slate-11">
      <div v-if="sourceId" class="flex justify-between items-start gap-2">
        <span class="font-medium flex-shrink-0">{{
          $t('CONVERSATION.AD_ID') || 'Ad ID:'
        }}</span>
        <span class="break-all text-right">{{ sourceId }}</span>
      </div>
      <div v-if="ctwaClid" class="flex justify-between items-start gap-2">
        <span class="font-medium flex-shrink-0">{{
          $t('CONVERSATION.CLICK_ID') || 'Click ID:'
        }}</span>
        <span class="break-all text-right">{{ ctwaClid }}</span>
      </div>
    </div>
  </div>
  <div v-else class="px-4 py-4 text-center text-sm text-n-slate-11">
    {{ $t('CUSTOM_ATTRIBUTES.NO_RECORDS_FOUND') || 'No records found' }}
  </div>
</template>
